<template>
    <div class="calendar_frame">
        <div class="calendar">
            <div class="container-fluid">
                <div class="row">
                    <div class="col-md-3 d-flex flex-column align-items-start">
                        <div class="mb-4">
                            <button class="btn btn_main d-flex align-items-center" @click="openAddEventForm">
                                <icon icon="Plus" class="me-2" style="width: 25px;"></icon>
                                <span>Vytvořit</span>
                            </button>
                        </div>
                        <FullCalendar id="mini_calendar" :options="miniCalendarOptions" v-if="!$store.getters.isMobile" />
                        <div class="mt-4 mb-4">
                            <h5>{{ $t("user.icals.myIcals") }}</h5>
                            <p v-if="!icals || icals.length === 0" class="text-muted mb-0" style="font-size: 14px;">
                                {{ $t("user.icals.noIcals") }}
                            </p>
                            <ul v-else class="list-group">
                                <li class="d-flex gap-2" v-for="ical in icals" :key="ical.id">
                                    <span :style="{ backgroundColor: ical.color }" class="ical_color"></span>
                                    <div class="form-check">
                                        <input type="checkbox" class="form-check-input" id="check"
                                            v-model="ical.is_active" @change="checkboxIcal(ical)">
                                        <label class="form-check-label" for="check">{{ ical.name }}</label>
                                    </div>
                                </li>
                            </ul>
                            <button class="btn btn_main mt-3 d-flex align-items-center" @click="openModal('ical')">
                                <icon icon="Settings" class="me-2"></icon>
                                <span>Nastavení</span>
                            </button>
                        </div>
                    </div>

                    <div class="col-md-9">
                        <FullCalendar id="calendar" ref="calendarRef" :options="calendarOptions" />
                    </div>
                </div>
            </div>

            <!-- Modal for events -->
            <div class="modal fade" id="eventModal" tabindex="-1" aria-hidden="true" ref="eventModal">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content modal_wrapper">
                        <div class="modal-header mx-auto border-0">
                            <h5 class="modal-title title">{{ modalTitle }}</h5>
                        </div>
                        <div class="modal-body">
                            <EventForm v-if="isEditing" :event="selectedEvent" @submit="saveEvent" />

                            <div v-else>
                                <div class="box">
                                    <h5>{{ selectedEvent.title }}</h5>
                                    <p>{{ formattedDateRange }}</p>
                                    <p v-if="selectedEvent.description">Popis: {{ selectedEvent.description }}</p>
                                    <p v-if="selectedEvent.location">Místo: {{ selectedEvent.location }}</p>

                                    <div v-if="!isDeleting">
                                        <div class="d-flex gap-2 justify-content-center flex-wrap">
                                            <button v-if="selectedEvent.ical_type !== 'external'"
                                                class="btn btn_main" @click="editEvent">Upravit</button>
                                            <button v-if="selectedEvent.ical_type !== 'external'"
                                                class="btn btn_secondary"
                                                @click="isDeleting = true">Smazat</button>
                                            <button class="btn btn_secondary"
                                                @click="closeModal('event')">Zavřít</button>
                                        </div>
                                    </div>
                                    <div v-else>
                                        <p>Opravdu si přejete smazat událost {{ selectedEvent.title }}?</p>
                                        <div class="d-flex gap-2 justify-content-center">
                                            <button class="btn btn_main" @click="deleteEvent">Ano</button>
                                            <button class="btn btn_secondary"
                                                @click="closeModal('event')">Ne</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modal for iCals -->
            <div class="modal fade" id="icalModal" tabindex="-1" aria-hidden="true" ref="icalModal">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content modal_wrapper">
                        <div class="modal-header mx-auto border-0">
                            <h5 class="modal-title title">{{ $t("user.icals.myIcals") }}</h5>
                        </div>
                        <div class="modal-body">
                            <div class="box">
                                <div v-if="!icals || icals.length === 0" class="text-center py-2">
                                    {{ $t("user.icals.noIcals") }}
                                </div>
                                <ul v-else class="list-group-item">
                                    <li v-for="ical in icals" :key="ical.id"
                                        class="list-group-item d-flex justify-content-between align-items-center">
                                        <div class="d-flex align-items-center gap-3">
                                            <!-- <input type="checkbox" class="form-check-input" v-model="ical.is_active" @change="checkboxIcal(ical)" /> -->
                                            <span :style="{ backgroundColor: ical.color }" class="ical_color"></span>
                                            <span>{{ ical.name }}</span>
                                        </div>
                                        <div class="d-flex gap-1">
                                            <button v-if="ical.ical_type === 'external'"
                                                class="btn btn_icon" @click="deleteIcal(ical)">
                                                <icon icon="Delete"></icon>
                                            </button>
                                            <button class="btn btn_icon" @click="openEditIcalModal(ical)">
                                                <icon icon="Edit"></icon>
                                            </button>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div class="modal-footer border-0 d-flex gap-2 justify-content-center">
                                <button type="button" class="btn btn_secondary"
                                    @click="closeModal('ical')">Zavřít</button>
                                <button type="button" class="btn btn_main" @click="openNewIcalModal">Přidat
                                    kalendář</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modal for editing or adding external iCal -->
            <div class="modal fade" id="editIcalModal" tabindex="-1" aria-hidden="true" ref="editIcalModal">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content modal_wrapper">
                        <div class="modal-header mx-auto border-0">
                            <h5 class="modal-title title">{{ modalTitle }}</h5>
                        </div>
                        <div class="modal-body">
                            <div class="box">
                                <form @submit.prevent="saveIcal">
                                    <div class="mb-3">
                                        <label class="form-label">Název</label>
                                        <input type="text" class="form-control" v-model="icalForm.name" required />
                                    </div>
                                    <div class="mb-3">
                                        <label class="form-label">URL adresa</label>
                                        <div class="d-flex align-items-center">
                                            <div class="input-group">
                                                <input v-if="icalForm.ical_type === 'external'" type="text"
                                                    class="form-control" v-model="icalForm.externalUrl" required />
                                                <input v-else-if="icalForm.id !== null" type="text" class="form-control"
                                                    :value="getFullIcalUrl(icalForm)" readonly />
                                                <input v-else type="text" class="form-control"
                                                    v-model="icalForm.externalUrl" required />
                                                <button v-if="isEditingIcal" type="button"
                                                    class="btn btn_icon" @click="copyIcalUrl">
                                                    <icon icon="Copy"></icon>
                                                </button>
                                            </div>
                                            <span v-if="copied" class="ms-2 small">Zkopírováno</span>
                                        </div>
                                    </div>
                                    <div class="mb-3">
                                        <label class="form-label">Barva kalendáře</label>
                                        <div class="color_picker_wrapper">
                                            <input type="color" v-model="icalForm.color" class="color_input" />
                                        </div>
                                    </div>
                                    <div class="form-check">
                                        <input v-model="icalForm.sendReminders" class="form-check-input" type="checkbox"
                                            id="checkReminders">
                                        <label class="form-check-label" for="checkReminders">Oznámení o
                                            událostech</label>
                                    </div>
                                    <div class="modal-footer border-0 d-flex gap-2 justify-content-center">
                                        <button type="button" class="btn btn_secondary"
                                            @click="closeModal('editIcal')">Zavřít</button>
                                        <button type="submit" class="btn btn_main">{{ $t('utils.submit') }}</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import multiMonthPlugin from "@fullcalendar/multimonth";
import timeGridPlugin from "@fullcalendar/timegrid";
import iCalendarPlugin from "@fullcalendar/icalendar";
import EventForm from "@/components/EventForm.vue";
import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min.js";
import Icon from '@/components/Icon.vue';

export default {
    components: {
        FullCalendar,
        EventForm,
        Icon,
    },
    data() {
        return {
            isEditing: false,
            isDeleting: false,
            newEvent: {},
            selectedEvent: {},
            eventModal: null,
            icalModal: null,
            editIcalModal: null,
            isEditingIcal: false,
            icals: [],
            icalForm: {
                id: null,
                name: "",
                ical_type: "external",
                url: "",
                externalUrl: "",
                sendReminders: false,
            },
            modalTitle: null,
            eventsByInstitution: {},   // Data z endpointu
            activeInstitutions: [],
            copied: false,
            miniCalendarOptions: {
                plugins: [dayGridPlugin, interactionPlugin],
                initialView: "dayGridMonth",
                locale: "cs",
                firstDay: 1,
                selectable: true,
                height: "auto",
                dateClick: this.changeDate, // Kliknuti v mini kalendáři
                headerToolbar: {
                    start: "title",
                    right: "prev,next",
                },
                dayCellContent: (arg) => {
                    return arg.dayNumberText.replace('.', ''); // Odstraneni tecky za cislem dne
                },
            },
            calendarOptions: {
                plugins: [dayGridPlugin, interactionPlugin, multiMonthPlugin, timeGridPlugin, iCalendarPlugin],
                initialView: "timeGridWeek",
                height: "auto", // Vyska kalendare
                editable: false, // Pretahovani udalosti
                selectable: true, // Umoznuje pridavat udalosti kliknutim
                firstDay: 1, // Nastavi pondeli jako prvni den tydne
                locale: "cs",
                nowIndicator: true, // Cervena linka na aktualnim case
                headerToolbar: {
                    start: "today prev,next",
                    center: "title",
                    right: "timeGridDay timeGridWeek dayGridMonth multiMonthYear",
                },
                buttonText: {
                    today: "Dnes",
                    day: "Den",
                    week: "Týden",
                    month: "Měsíc",
                    year: "Rok",
                },
                allDayText: "", // odstraneni all-day
                views: { // Nastaveni casove linky pro jednotlive views
                    timeGridWeek: {
                        slotLabelFormat: { hour: "2-digit", minute: "2-digit", hour12: false }
                    },
                    timeGridDay: {
                        slotLabelFormat: { hour: "2-digit", minute: "2-digit", hour12: false }
                    }
                },
                dayHeaderFormat: { weekday: "short" }, // Prepnuti na dlouhe nazvy dni
                dayHeaderContent: (args) => {
                    const date = args.date;
                    const dayName = args.text.charAt(0).toUpperCase() + args.text.slice(1);
                    const dayNumber = date.getDate();

                    return {
                        html: `<div>${dayName}</div><div style="font-size: 1.3em; ">${dayNumber}</div>`
                    };
                },
                dayCellContent: (arg) => {
                    return arg.dayNumberText.replace('.', ''); // Odstraneni tecky za cislem dne
                },
                eventClick: this.handleEventClick, // Kliknuti na udalost
                select: this.handleDateSelect, // Kliknuti do kalendare na pridani nove udalosti
                events: [],
            }
        };
    },
    mounted() {
        this.eventModal = new Modal(this.$refs.eventModal, { backdrop: "static" });
        this.icalModal = new Modal(this.$refs.icalModal, { backdrop: "static" });
        this.editIcalModal = new Modal(this.$refs.editIcalModal, { backdrop: "static" });
        this.getEvents();
        this.getIcals();
    },
    methods: {
        // Redirects the main calendar to the date based on the click in the miniCalendar
        changeDate(info) {
            if (this.$refs.calendarRef) {
                this.$refs.calendarRef.getApi().gotoDate(info.dateStr);
            }
        },

        getEvents() {
            const api = this.$store.getters.api;

            // Internal events
            api.get("/user/events/institutions")
                .then(response => {
                    const internEvents = response.data;

                    // External events
                    api.get("/ical/external/events")
                        .then(response => {
                            const externEvents = response.data;

                            this.eventsByInstitution = {
                                ...internEvents,
                                ...externEvents
                            };

                            this.updateMergedEvents();
                        })
                })
        },

        // Loads the list of calendars (icals)
        getIcals() {
            this.$store.getters.api.get("/ical/user/all")
                .then(response => {
                    this.icals = response.data;
                })
        },

        openModal(type) {
            if (type === "event") {
                this.eventModal.show();
            } else if (type === "ical") {
                this.icalModal.show();
            }
        },
        closeModal(type) {
            if (type === "event") {
                this.eventModal.hide();
            } else if (type === "ical") {
                this.icalModal.hide();
            } else if (type === "editIcal") {
                this.editIcalModal.hide();
            }
        },

        // Used to toggle individual calendars on and off based on institution
        checkboxIcal(ical) {
            const instKey = ical.match_key ? ical.match_key : ical.id;

            if (ical.is_active) {
                if (!this.activeInstitutions.includes(instKey)) {
                    this.activeInstitutions.push(instKey);
                }
            } else {
                this.activeInstitutions = this.activeInstitutions.filter(key => key !== instKey);
            }
            this.updateMergedEvents();

            this.$store.getters.api.put(`/ical/update/${ical.id}`, { is_active: ical.is_active })
        },

        // Merges events from all institutions into calendarOptions.events
        updateMergedEvents() {
            let merged = [];
            for (const key of this.activeInstitutions) {
                if (this.eventsByInstitution[key]) {
                    merged = merged.concat(this.eventsByInstitution[key].events);
                }
            }
            this.calendarOptions.events = merged.map(event => {
                let endCalendar = event.end;
                if (event.allDay) {
                    const endDateObj = new Date(event.end);
                    endDateObj.setDate(endDateObj.getDate() + 1);
                    endCalendar = endDateObj.toISOString().split("T")[0];
                }
                return {
                    ...event,
                    start: event.start,
                    end: endCalendar,
                    endForm: event.end,
                    allDay: event.allDay,
                    ical_type: event.ical_type || null,
                    color: event.color,
                };
            });
        },

        //  Click on an event to open a modal with event details
        handleEventClick(info) {
            this.selectedEvent = {
                id: info.event.id,
                title: info.event.title,
                start: info.event.startStr,
                end: info.event.endStr,
                allDay: info.event.allDay,
                startDate: info.event.startStr.split("T")[0],
                startTime: info.event.allDay ? "" : info.event.startStr.split("T")[1].substring(0, 5),
                endDate: info.event.extendedProps.endForm.split("T")[0],
                endTime: info.event.allDay ? "" : info.event.extendedProps.endForm.split("T")[1]?.substring(0, 5),
                slug: info.event.extendedProps.slug,
                description: info.event.extendedProps.description,
                location: info.event.extendedProps.location,
                ical_type: info.event.extendedProps.ical_type || null
            };
            this.modalTitle = this.$t("general.event")
            this.isEditing = false;
            this.isDeleting = false;
            this.openModal("event");
        },

        // Select a date and time in the calendar to open a modal for a new event
        handleDateSelect(selectionInfo) {
            this.selectedEvent = {
                title: "",
                description: "",
                location: "",
                allDay: selectionInfo.allDay,
                startDate: selectionInfo.startStr.split("T")[0],
                startTime: selectionInfo.allDay ? "" : selectionInfo.startStr.split("T")[1]?.substring(0, 5),
                endDate: selectionInfo.startStr.split("T")[0],
                endTime: selectionInfo.allDay ? "" : selectionInfo.endStr ? selectionInfo.endStr.split("T")[1]?.substring(0, 5) : "",
            };

            this.modalTitle = this.$t("general.addEvent");
            this.isEditing = true;
            this.openModal("event");
        },

        // Initializes the creation of a new event and opens the modal for it
        openAddEventForm() {
            this.selectedEvent = {
                title: "",
                description: "",
                location: "",
                allDay: false,
                startDate: "",
                startTime: "00:00",
                endDate: "",
                endTime: "00:00"
            };
            this.modalTitle = this.$t("general.addEvent");
            this.isEditing = true;
            this.openModal("event");
        },
        addEvent(eventData) {
            if (!eventData.title.trim() || !eventData.startDate || !eventData.endDate) {
                alert("Vyplňte povinná pole: Název, datum začátku a konce události");
                return;
            }

            // Set datetime format
            const full_start_time = eventData.allDay ? `${eventData.startDate}T00:00:00` : `${eventData.startDate}T${eventData.startTime}:00`;
            const full_end_time = eventData.allDay ? `${eventData.endDate}T23:59:00` : `${eventData.endDate}T${eventData.endTime}:00`;

            const formData = {
                title: eventData.title,
                description: eventData.description || null,
                location: eventData.location || null,
                all_day: eventData.allDay,
                start_time: full_start_time,
                end_time: full_end_time,
            };

            this.$store.getters.api.post(`/event/add`, formData)
                .then(() => {
                    this.closeModal("event");
                    this.getEvents();
                })
        },
        editEvent(eventData) {
            this.modalTitle = this.$t("general.editEvent");

            if (!eventData || !eventData.id) {
                this.isEditing = true;
                return;
            }

            const full_start_time = eventData.allDay ? `${eventData.startDate}T00:00:00` : `${eventData.startDate}T${eventData.startTime}:00`;
            const full_end_time = eventData.allDay ? `${eventData.endDate}T23:59:00` : `${eventData.endDate}T${eventData.endTime}:00`;

            const formData = {
                title: eventData.title,
                description: eventData.description || null,
                location: eventData.location || null,
                all_day: eventData.allDay,
                start_time: full_start_time,
                end_time: full_end_time,
            };

            this.$store.getters.api.put(`/event/edit/${this.selectedEvent.slug}`, formData)
                .then(() => {
                    this.closeModal("event");
                    this.getEvents();
                })
        },
        saveEvent(eventData) {
            if (eventData.id) {
                this.editEvent(eventData);
            } else {
                this.addEvent(eventData);
            }
        },
        deleteEvent() {
            this.$store.getters.api.delete(`/event/delete/${this.selectedEvent.slug}`)
                .then(() => {
                    this.closeModal("event");
                    this.getEvents();
                })
        },

        // Open a modal for editing an existing iCal
        openEditIcalModal(ical) {
            this.icalForm = {
                id: ical.id,
                name: ical.name,
                ical_type: ical.ical_type,
                url: ical.url,
                externalUrl: ical.external_url,
                color: ical.color,
                sendReminders: ical.send_reminders,
            };
            this.modalTitle = this.$t("user.icals.editIcal");
            this.isEditingIcal = true;
            this.closeModal("ical");
            this.editIcalModal.show();
        },

        // Open a modal to create a new iCal
        openNewIcalModal() {
            this.icalForm = {
                id: null,
                name: "",
                ical_type: "",
                url: "",
                externalUrl: "",
                color: "#0099e6",
                sendReminders: false,
            };
            this.modalTitle = this.$t("user.icals.newIcal");
            this.isEditingIcal = false;
            this.closeModal("ical");
            this.editIcalModal.show();
        },

        // Save changes if isEditingIcal is true, otherwise create a new iCal
        saveIcal() {
            const api = this.$store.getters.api;
            if (this.isEditingIcal) {
                api.patch(`/ical/external/edit/${this.icalForm.id}`, this.icalForm)
                    .then(() => {
                        this.getIcals();
                        this.getEvents();
                        this.closeModal("editIcal");
                    })
            } else {
                api.post("/ical/external/add", this.icalForm)
                    .then(() => {
                        this.getIcals();
                        this.closeModal("editIcal");
                    })
            }
        },

        // Delete iCal
        deleteIcal(ical) {
            this.$store.getters.api.delete(`/ical/external/delete/${ical.id}`)
                .then(() => {
                    this.getIcals();
                })
        },

        // Full URL for intern iCals
        getFullIcalUrl(ical) {
            const baseUrl = window.location.origin;
            return ical.ical_type === "external" ? ical.externalUrl : baseUrl + ical.url;
        },

        // Copy URL of iCal
        copyIcalUrl() {
            const url = this.getFullIcalUrl(this.icalForm);
            navigator.clipboard.writeText(url)
                .then(() => {
                    this.copied = true;
                    setTimeout(() => {
                        this.copied = false;
                    }, 1000);
                });
        },
    },
    computed: {
        // Return a formatted date and time string based on event type
        formattedDateRange() {
            const startDate = new Date(this.selectedEvent.startDate);
            const endDate = new Date(this.selectedEvent.endDate);
            const startTime = this.selectedEvent.startTime;
            const endTime = this.selectedEvent.endTime;
            const sameDay = startDate.toDateString() === endDate.toDateString();

            const formatDate = (dateStr) => {
                const date = new Date(dateStr);
                return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
            }

            if (this.selectedEvent.allDay) {
                if (sameDay) {
                    return `${formatDate(this.selectedEvent.startDate)} - celodenní`;
                } else {
                    return `${formatDate(this.selectedEvent.startDate)} - ${this.selectedEvent.endDate} - celodenní`;
                }
            } else {
                if (sameDay) {
                    return `${formatDate(this.selectedEvent.startDate)} - ${startTime}-${endTime}`;
                } else {
                    return `${formatDate(this.selectedEvent.startDate)}, ${startTime} - ${formatDate(this.selectedEvent.endDate)}, ${endTime}`;
                }
            }
        }
    },
    watch: {
        // Watches for changes in icals and updates active institutions, then re-merges events accordingly
        icals: {
            handler(newVal) {
                this.activeInstitutions = newVal
                    .filter(ical => ical.is_active)
                    .map(ical => ical.match_key ? ical.match_key : ical.token);
                this.updateMergedEvents();
            },
            deep: true,
            immediate: true
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/scss/abstracts/variables";

.calendar_frame {
    background: transparent;
    padding: 0;
    border-radius: $radius-lg;
}

.calendar {
    padding: 20px;
    background-color: $surface;
    border: 1px solid $border;
    box-shadow: $shadow-sm;
    border-radius: $radius-lg;
    color: $text;
}

.ical_color {
    width: 20px;
    height: 20px;
    border-radius: 50%;
}

.color_input {
    width: 25px;
    height: 25px;
    border-radius: 50%;
    border: none;
    padding: 0;
    cursor: pointer;
}

// Unified icon button (calendar/iCal edit/delete/copy) — transparent with a
// themed icon, matching .btn_edit/.btn_delete used elsewhere in the app.
.btn_icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    border-radius: $radius-sm;
    padding: 5px;
    color: $accent;
    transition: background-color 0.2s ease;

    svg {
        stroke: $accent;
        width: 24px;
        height: 24px;
    }

    &:hover {
        background: $surface-2;
    }
}

/* menší font */
:deep(.fc-header-toolbar .fc-toolbar-title) {
    font-size: 1.6rem;
}

/* Odstraneni ramecku */
#mini_calendar :deep(.fc-scrollgrid),
#mini_calendar :deep(.fc-scrollgrid td),
#mini_calendar :deep(.fc-scrollgrid th) {
    border: none;
}

#mini_calendar :deep(.fc-daygrid-day-top) {
    padding: 5px;
}

#mini_calendar :deep(.fc-daygrid-day-events) {
    display: none;
}

#mini_calendar :deep(.fc-daygrid-day-frame):hover {
    cursor: pointer;
    background-color: rgba(128, 128, 128, 0.1);
    border-radius: 50%;
}

#mini_calendar :deep(.fc-toolbar-title) {
    color: black;
    font-weight: bold;
    text-transform: capitalize;
    font-size: 18px;
}

#calendar :deep(.fc-toolbar-title) {
    color: black;
    font-weight: bold;
    text-transform: capitalize;
}

/* Timeline */
#calendar :deep(.fc-timegrid-slot) {
    color: black;
}

:deep(.fc-daygrid-day-top) {
    display: flex;
    justify-content: center;
    align-items: center;
}

/* Cisla v mriyce kalendare */
:deep(.fc-daygrid-day-number) {
    color: black;
    text-decoration: none;
}

:deep(.fc-col-header-cell-cushion) {
    text-decoration: none;
    color: black;
}

/* Tlacitka */
:deep(.fc .fc-button-primary) {
    background-color: $dark-purple;
    border: none;
    padding: 5px 20px;
    font-weight: 700;
    border-radius: $radius-md;
}

:deep(.fc .fc-button-primary:not(:disabled).fc-button-active) {
    background-color: $grey;
    color: $black;
}

// Tlacitka active
:deep(.fc .fc-button-primary:active) {
    background-color: $dark-purple;
    color: $white;
    padding: 5px 20px;
}

/* Tlacitko Dnes disabled */
:deep(.fc .fc-button-primary:disabled) {
    background-color: $grey;
    color: black;
    border: $grey 1px solid;
    padding: 5px 20px;
}

/* Dnesni datum */
:deep(.fc .fc-daygrid-day.fc-day-today) {
    background-color: $grey;
    opacity: 0.9;

    .fc-daygrid-day-number {
        font-weight: 700;
    }
}

/* Dalsi a predchozi tlacitka */
:deep(.fc-next-button),
:deep(.fc-prev-button) {
    background-color: $dark-purple;
    border: none;
    border-radius: $radius-md;
    padding: 5px 10px !important;

    &:hover {
        background-color: $black-purple;
    }
}

// Zvýraznění dnešního dne v polích velkého kalendáře
:deep(.fc-timegrid-col.fc-day-today) {
    background-color: $light-grey;
}


/* Odstraneni delici cary po 30minutach */
:deep(.fc-timegrid-slot.fc-timegrid-slot-label.fc-timegrid-slot-minor) {
    border-top: none;
}

:deep(.fc-timegrid-slot.fc-timegrid-slot-lane.fc-timegrid-slot-minor) {
    border-top: none;
}

:deep(.fc-timegrid-slot-lane:hover) {
    background-color: $grey;
}

:deep(.fc .fc-highlight) {
    background-color: $grey;

}

// RESPONZIVITA

:deep(.fc-header-toolbar) {
    flex-wrap: wrap;
    /* povolí zalomení řádků */
}

@media (max-width: 1124px) {
    :deep(.fc-header-toolbar) {
        justify-content: center;
        text-align: center;
    }

    :deep(.fc-header-toolbar .fc-toolbar-chunk) {
        flex: 1 1 100%;
        /* každý chunk na celý řádek */
        text-align: center;
        margin-bottom: 0.5rem;
    }

    :deep(.fc-header-toolbar .fc-toolbar-title) {
        font-size: 1.1rem;
        /* menší font na mobilu */
    }
}

@media (min-width: 1124px) and (max-width: 1440px) {

    :deep(#calendar .fc-header-toolbar .fc-toolbar-chunk:first-child),
    :deep(#calendar .fc-header-toolbar .fc-toolbar-chunk:nth-child(2)) {
        order: 1;
        flex: 0 0 auto;
        /* první řádek */
    }

    :deep(#calendar .fc-header-toolbar .fc-toolbar-chunk:last-child) {
        order: 2;
        /* druhý řádek */
        flex: 1 1 100%;
        text-align: center;
        margin-top: 1rem;
    }

    :deep(#calendar .fc-header-toolbar .fc-toolbar-title) {
        margin: 0 1rem;
    }

    :deep(.fc-prev-button),
    :deep(.fc-next-button) {
        padding: 5px 6px !important;
    }
}
</style>